import { useCallback, useEffect, useRef, useState } from 'react';
import { listMessagesAfter, openSseStream, sendMessage } from '../api/orchestration';
import type { ConnectionState, SseEvent } from '../interfaces/chat';

/**
 * Manages the SSE transport for a session. Sessions are durable server-side;
 * the SSE connection is a transient transport that the server's idle janitor
 * may close. We reconnect on the next send rather than surfacing a 4xx.
 * Ported from the archer-ai example client.
 *
 * Two multi-pod failure modes this hook guards against:
 *  - The pod holding the SSE connection dies without the socket closing
 *    cleanly (e.g. behind a load balancer with a long idle timeout). A
 *    watchdog forces a reconnect if no bytes arrive for HEARTBEAT_TIMEOUT_MS.
 *    The backend emits an `: heartbeat` SSE comment every 30s (orchestration
 *    -service `SseEmitterJanitor`); the window here is 3x that to absorb
 *    normal jitter without false-positiving.
 *  - The pod processing the turn dies after accepting it. No SSE event will
 *    ever arrive for that turn, so a per-turn timer bounds how long the UI
 *    waits before giving up and letting the user retry. On every reconnect
 *    while a turn is still outstanding, we also proactively check durable
 *    storage (`GET /sessions/{id}/messages?after=`) for a reply that
 *    finished — and was persisted — while the connection was down; Redis
 *    pub/sub only fans a completed turn out to the pod that owned the live
 *    SSE connection at that moment, so a dropped connection means that live
 *    delivery is simply lost and must be recovered this way.
 */
const HEARTBEAT_TIMEOUT_MS = 90_000;
const RESPONSE_TIMEOUT_MS = 60_000;

export function useSseStream(sessionId: string | null) {
  const [connectionState, setConnectionState] = useState<ConnectionState>({ status: 'disconnected' });
  const [sseEvents, setSseEvents] = useState<SseEvent[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const connectedRef = useRef<Promise<void> | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const responseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The most recently sent user message's id, and whether its reply is still
  // outstanding — used to recover a missed reply after a reconnect.
  const lastUserMessageIdRef = useRef<string | null>(null);
  const pendingRecoveryRef = useRef(false);

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = null;
  }, []);

  const clearResponseTimeout = useCallback(() => {
    if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
    responseTimeoutRef.current = null;
  }, []);

  const teardownConnection = useCallback(() => {
    controllerRef.current = null;
    connectedRef.current = null;
    clearWatchdog();
  }, [clearWatchdog]);

  const armWatchdog = useCallback(() => {
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      // No activity from the server within the window — assume the pod
      // holding this connection died without closing the socket. Tear down
      // so the existing reconnect-on-disconnect effect picks it back up.
      controllerRef.current?.abort();
      teardownConnection();
      setConnectionState({ status: 'disconnected' });
    }, HEARTBEAT_TIMEOUT_MS);
  }, [clearWatchdog, teardownConnection]);

  // Best-effort check of durable storage for a reply that finished while the
  // SSE connection was down. Only clears the outstanding-turn state if a
  // reply is actually found — if the turn is genuinely still in flight, this
  // finds nothing and leaves isStreaming/pendingRecovery alone so the normal
  // completion (now that the connection is live again) or the response
  // timeout can still take over.
  const attemptRecovery = useCallback(async (sid: string) => {
    const userMessageId = lastUserMessageIdRef.current;
    if (!pendingRecoveryRef.current || !userMessageId) return;
    try {
      const messages = await listMessagesAfter(sid, userMessageId);
      const reply = messages.find((m) => m.role.toUpperCase() === 'ASSISTANT');
      if (!reply) return;

      const content = reply.content as { type?: string; text?: string };
      const text = typeof content?.text === 'string' ? content.text : JSON.stringify(content);
      const now = new Date();
      setSseEvents((prev) => [
        ...prev,
        { id: crypto.randomUUID(), type: 'chat.block', data: JSON.stringify({ content: text }), timestamp: now },
        { id: crypto.randomUUID(), type: 'chat.completed', data: '{}', timestamp: now },
      ]);
      pendingRecoveryRef.current = false;
      setIsStreaming(false);
      clearResponseTimeout();
    } catch {
      // 404 (message not found yet) or network error — leave state as-is.
    }
  }, [clearResponseTimeout]);

  const connect = useCallback(() => {
    if (!sessionId || controllerRef.current) return;

    setConnectionState({ status: 'connecting' });

    const { controller, connected } = openSseStream(
      sessionId,
      (event) => {
        setSseEvents((prev) => [...prev, event]);
        if (isPerTurnTerminal(event.type)) {
          setIsStreaming(false);
          pendingRecoveryRef.current = false;
          clearResponseTimeout();
        }
      },
      (error) => {
        setConnectionState({ status: 'error', error });
        setIsStreaming(false);
        teardownConnection();
      },
      () => {
        setIsStreaming(false);
        teardownConnection();
        // A real TCP close, not just a per-turn [DONE] — reset to
        // 'disconnected' so the reconnect-on-disconnect effect in useChat
        // opens a fresh connection for the next turn (mirrors the watchdog's
        // teardown above, which already does this).
        setConnectionState({ status: 'disconnected' });
      },
      () => armWatchdog(),
    );

    controllerRef.current = controller;
    armWatchdog();
    connectedRef.current = connected.then(() => {
      setConnectionState({ status: 'connected' });
      void attemptRecovery(sessionId);
    });
  }, [sessionId, teardownConnection, armWatchdog, clearResponseTimeout, attemptRecovery]);

  const send = useCallback(
    async (text: string, callbackId?: string | null) => {
      if (!sessionId) return;
      if (!controllerRef.current) connect();
      if (connectedRef.current) await connectedRef.current;
      setStreamError(null);
      setIsStreaming(true);
      pendingRecoveryRef.current = true;
      clearResponseTimeout();
      responseTimeoutRef.current = setTimeout(() => {
        // The turn was accepted but nothing came back in time — most likely
        // the pod that picked it up died mid-processing. Stop waiting so the
        // user isn't stuck; the session and connection are left intact.
        setIsStreaming(false);
        pendingRecoveryRef.current = false;
        setStreamError('The assistant is taking longer than expected to respond. Please try again.');
      }, RESPONSE_TIMEOUT_MS);
      lastUserMessageIdRef.current = await sendMessage(sessionId, text, callbackId);
    },
    [sessionId, connect, clearResponseTimeout],
  );

  const disconnect = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    connectedRef.current = null;
    clearWatchdog();
    clearResponseTimeout();
    pendingRecoveryRef.current = false;
    lastUserMessageIdRef.current = null;
    setConnectionState({ status: 'disconnected' });
    setIsStreaming(false);
  }, [clearWatchdog, clearResponseTimeout]);

  const clearEvents = useCallback(() => setSseEvents([]), []);

  useEffect(() => () => {
    clearWatchdog();
    clearResponseTimeout();
  }, [clearWatchdog, clearResponseTimeout]);

  return {
    connectionState,
    sseEvents,
    isStreaming,
    streamError,
    connect,
    send,
    disconnect,
    clearEvents,
  };
}

/** Per-turn terminal SSE types — the turn is done but the connection stays open. */
function isPerTurnTerminal(type: string | null): boolean {
  return (
    type === 'done' ||
    type === 'chat.done' ||
    type === 'chat.completed' ||
    type === 'chat.error' ||
    type === 'chat.halted' ||
    type === 'chat.escalated'
  );
}
